<template>
  <div>
    <p v-if="isAssistantActive" class="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
      {{ announcement }}
    </p>
    <Transition name="assistant-fade">
      <div v-if="showBlock" class="writingAssistant" :class="{ '-error': hasError }">
        <div class="writingAssistant__header">
          <div class="writingAssistant__title">
            <FontAwesomeIcon icon="fa-light fa-sparkles" class="writingAssistant__titleIcon" aria-hidden="true" />
            {{ $t("writing_assistant.title") }}
            <span v-if="isLoading" class="writingAssistant__loader" role="status" aria-live="polite">
              <span class="writingAssistant__dot" aria-hidden="true"></span>
              <span class="writingAssistant__dot" aria-hidden="true"></span>
              <span class="writingAssistant__dot" aria-hidden="true"></span>
              <span class="sr-only">{{ $t("writing_assistant.loading") }}</span>
            </span>
          </div>
          <button
            v-if="visibleQuestions.length > 0"
            type="button"
            class="writingAssistant__close"
            :aria-label="$t('writing_assistant.disable')"
            @click="disableAssistant"
          >
            <FontAwesomeIcon icon="fa-light fa-xmark" aria-hidden="true" />
          </button>
        </div>

        <TransitionGroup name="question-slide" tag="ul" class="writingAssistant__list">
          <li v-for="question in visibleQuestions" :key="question.id" class="writingAssistant__item">
            <FontAwesomeIcon icon="fa-light fa-circle-question" class="writingAssistant__itemIcon" aria-hidden="true" />
            <span class="writingAssistant__itemText">{{ question.text }}</span>
            <div class="writingAssistant__itemActions">
              <button
                type="button"
                class="writingAssistant__action writingAssistant__action--helpful"
                :aria-label="$t('writing_assistant.helpful')"
                :aria-pressed="helpfulQuestions.includes(question.id)"
                @click="markHelpful(question)"
              >
                <FontAwesomeIcon icon="fa-light fa-thumbs-up" aria-hidden="true" />
              </button>
              <button
                type="button"
                class="writingAssistant__action writingAssistant__action--dismiss"
                :aria-label="$t('writing_assistant.dismiss')"
                @click="dismissQuestion(question)"
              >
                <FontAwesomeIcon icon="fa-light fa-eye-slash" aria-hidden="true" />
              </button>
            </div>
          </li>
        </TransitionGroup>

        <p
          v-if="hasError && !isLoading"
          class="writingAssistant__status -error"
          :class="{ '-spaced': visibleQuestions.length > 0 }"
          aria-hidden="true"
        >
          {{ errorMessage || $t("writing_assistant.unavailable") }}
        </p>
      </div>
    </Transition>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { writingAssistantConstantes } from "@/constantes/writingAssistant";
import { writingAssistantManager } from "@/services/form/writing-assistant-manager";
import { sanitizeText } from "@/services/form/writing-assistant-sanitizer";
import { useWritingAssistantStore } from "@/store/writing-assistant/writing-assistant";

export default {
  name: "WritingAssistantComponent",
  components: { FontAwesomeIcon },
  inject: {
    formContext: { default: null },
  },
  props: {
    isActivityComplete: {
      type: Boolean,
      required: false,
      default: false,
    },
    inputValue: {
      type: String,
      required: false,
      default: "",
    },
    fieldData: {
      type: Object,
      required: true,
    },
    isDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ["helpful", "dismiss", "disabled"],
  data() {
    return {
      questions: [],
      dismissed: [],
      helpfulQuestions: [],
      isLoading: false,
      hasError: false,
      errorMessage: null,
      announcement: "",
      attemptsRemaining: writingAssistantConstantes.MAX_ATTEMPTS,
      lastSuccessLength: null,
      lastSuccessText: null,
      serverMinLength: null,
      isContractRefused: false,
    };
  },
  computed: {
    ...mapState(useWritingAssistantStore, ["pausedFields"]),
    fieldKey() {
      return this.fieldData?.field_type ?? "default";
    },
    contractCryptedId() {
      return this.formContext?.contractCryptedId || null;
    },
    activityId() {
      return this.formContext?.activityId ?? null;
    },
    draftId() {
      return this.formContext?.activityDraftId ?? null;
    },
    scopeKey() {
      const scope = this.activityId ?? this.draftId;
      return scope === null ? null : String(scope);
    },
    pauseKey() {
      return this.scopeKey + writingAssistantConstantes.PAUSE_KEY_SEPARATOR + this.fieldKey;
    },
    isPaused() {
      return this.pauseKey in this.pausedFields;
    },
    isAssistantActive() {
      return !this.isDisabled && !this.isActivityComplete && !this.isAiAssistantDisabled;
    },
    isAiAssistantDisabled() {
      return !!this.formContext?.aiAssistantDisabled;
    },
    visibleQuestions() {
      return this.questions
        .filter((q) => !this.dismissed.includes(q.id))
        .slice(0, writingAssistantConstantes.MAX_QUESTIONS);
    },
    showBlock() {
      return this.isAssistantActive && (this.isLoading || this.hasError || this.visibleQuestions.length > 0);
    },
    sanitizedText() {
      return sanitizeText(this.inputValue);
    },
    requiredLength() {
      if (this.serverMinLength !== null) return this.serverMinLength;
      const thresholds = writingAssistantConstantes.LENGTH_THRESHOLDS;
      const used = writingAssistantConstantes.MAX_ATTEMPTS - this.attemptsRemaining;
      const threshold = thresholds[Math.min(used, thresholds.length - 1)];
      if (this.lastSuccessLength === null) return threshold;
      return Math.max(threshold, this.lastSuccessLength + writingAssistantConstantes.MIN_DELTA);
    },
    canAnswer() {
      return this.isAssistantActive && !this.isPaused && !this.isContractRefused && this.contractCryptedId !== null;
    },
    canAsk() {
      return (
        this.isAssistantActive &&
        this.contractCryptedId !== null &&
        this.scopeKey !== null &&
        !this.isContractRefused &&
        !this.isLoading &&
        this.attemptsRemaining > 0 &&
        this.sanitizedText.length >= this.requiredLength &&
        this.sanitizedText !== this.lastSuccessText
      );
    },
  },
  watch: {
    inputValue(newVal) {
      if (sanitizeText(newVal).length === 0) {
        this.reset();
        return;
      }
      if (!this.isContractRefused) {
        this.clearError();
      }
      this.scheduleAnalysis();
    },
    isActivityComplete(complete) {
      if (complete) {
        this.reset();
      }
    },
    isDisabled(disabled) {
      if (disabled) {
        this.reset();
      }
    },
    isAiAssistantDisabled(disabled) {
      if (disabled) {
        this.reset();
      }
    },
    contractCryptedId() {
      this.reset();
      this.isContractRefused = false;
      if (this.isPaused) {
        this.attemptsRemaining = 0;
      } else {
        this.rearm();
      }
    },
    scopeKey(newKey, oldKey) {
      if (newKey !== null && oldKey !== null && oldKey === this.draftId) {
        const separator = writingAssistantConstantes.PAUSE_KEY_SEPARATOR;
        this.movePause(oldKey + separator + this.fieldKey, newKey + separator + this.fieldKey);
      }
    },
    isPaused(paused) {
      if (!paused) {
        this.rearm();
      }
    },
  },
  created() {
    this.debounceTimer = null;
    this.requestId = 0;
    this.pendingEvaluation = false;
    this.resumeExpired();
    if (this.isPaused) {
      this.attemptsRemaining = 0;
    }
  },
  beforeUnmount() {
    this.clearDebounce();
    writingAssistantManager.stop();
  },
  methods: {
    ...mapActions(useWritingAssistantStore, ["pauseField", "movePause", "resumeExpired", "requestInfoOpen"]),
    async onUserInput() {
      await this.$nextTick();
      if (this.canAnswer && this.sanitizedText.length > 0) {
        this.requestInfoOpen();
      }
    },
    scheduleAnalysis() {
      this.clearDebounce();
      this.debounceTimer = setTimeout(this.onDebounceElapsed, writingAssistantConstantes.DEBOUNCE_DELAY);
    },
    clearDebounce() {
      if (this.debounceTimer !== null) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
    },
    async onDebounceElapsed() {
      this.debounceTimer = null;
      if (this.isLoading) {
        this.pendingEvaluation = true;
        return;
      }
      if (!this.canAsk) return;
      await this.analyse();
    },
    async analyse() {
      const requestId = ++this.requestId;
      this.isLoading = true;
      this.clearError();

      const text = this.inputValue;
      const payload = { ...this.fieldData, text, contract: this.contractCryptedId };
      if (this.activityId !== null) payload.activity = this.activityId;
      if (this.draftId !== null) payload.draft = this.draftId;
      payload.activity_title = this.formContext.objectTitle;

      const result = await writingAssistantManager.askQuestions(payload);

      if (requestId !== this.requestId) return;

      this.isLoading = false;
      this.announcement = "";
      if (result.status === "success") {
        this.applySuccess(result, sanitizeText(text));
      } else if (result.status === "error") {
        this.applyError(result);
      }

      if (this.pendingEvaluation) {
        this.pendingEvaluation = false;
        if (!this.hasError || result.kind === "busy") this.scheduleAnalysis();
      }
    },
    applySuccess(result, sanitized) {
      this.questions = result.questions;
      this.dismissed = [];
      this.helpfulQuestions = [];
      this.lastSuccessLength = sanitized.length;
      this.lastSuccessText = sanitized;
      this.attemptsRemaining =
        typeof result.quota?.attemptsRemaining === "number"
          ? result.quota.attemptsRemaining
          : Math.max(0, this.attemptsRemaining - 1);
      this.serverMinLength = typeof result.quota?.nextMinLength === "number" ? result.quota.nextMinLength : null;
      if (this.attemptsRemaining === 0) {
        this.pauseField(this.pauseKey, result.resetAt);
      }
      const count = this.visibleQuestions.length;
      this.announcement = count > 0 ? this.$t("writing_assistant.questions_announce", { count }, count) : "";
    },
    applyError(result) {
      if (result.kind === "quota") {
        this.attemptsRemaining = 0;
        this.pauseField(this.pauseKey, result.resetAt);
        return;
      }
      if (result.kind === "busy") {
        this.showError(result.message);
        return;
      }
      if (result.kind === "forbidden") {
        this.isContractRefused = true;
        this.showError(result.message);
        return;
      }
      if (result.kind === "tooShort") {
        this.serverMinLength = result.minLength;
        if (result.attemptsRemaining !== null) {
          this.attemptsRemaining = result.attemptsRemaining;
        }
        if (result.attemptsRemaining === 0) {
          this.pauseField(this.pauseKey, result.resetAt);
        }
        return;
      }
      this.showError(null);
    },
    showError(message) {
      this.hasError = true;
      this.errorMessage = message || null;
      this.announcement = this.errorMessage ?? this.$t("writing_assistant.unavailable");
    },
    clearError() {
      this.hasError = false;
      this.errorMessage = null;
    },
    rearm() {
      this.attemptsRemaining = writingAssistantConstantes.MAX_ATTEMPTS;
      this.lastSuccessLength = null;
      this.lastSuccessText = null;
      this.serverMinLength = null;
    },
    reset() {
      this.clearDebounce();
      this.requestId++;
      this.pendingEvaluation = false;
      this.isLoading = false;
      this.questions = [];
      this.dismissed = [];
      this.helpfulQuestions = [];
      this.clearError();
      this.announcement = "";
      writingAssistantManager.stop();
    },
    disableAssistant() {
      this.reset();
      this.$emit("disabled");
    },
    dismissQuestion(question) {
      this.dismissed.push(question.id);
      this.$emit("dismiss", question);
    },
    markHelpful(question) {
      if (!this.helpfulQuestions.includes(question.id)) {
        this.helpfulQuestions.push(question.id);
      }
      this.$emit("helpful", question);
      this.dismissed.push(question.id);
    },
  },
};
</script>

<style lang="scss" scoped>
.writingAssistant {
  margin-top: 8px;
  border-radius: $radius;
  border-left: 4px solid $ia-color;
  background: $ia-color-bg;
  color: $ia-color-text;
  padding: 12px 14px;
  min-width: 0;

  @include dark-theme {
    background: $ia-color-bg-dark;
    color: $ia-color-bg;
    border-color: $ia-color-bg;
  }

  &.-error {
    border-left-color: $ia-color-error;

    @include dark-theme {
      border-color: $ia-color-error-dark;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 10px;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 700;
    font-size: 13px;
  }

  &__titleIcon {
    width: 14px;
  }

  &__close {
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    padding: 2px 4px;
    line-height: 1;

    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 13px;
    line-height: 1.4;
  }

  &__itemIcon {
    width: 13px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__itemText {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  &__itemActions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
    margin-left: 4px;
  }

  &__action {
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    padding: 2px;
    line-height: 1;

    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
      border-radius: 2px;
    }

    &[aria-pressed="true"] {
      font-weight: 700;
    }

    &--helpful:hover {
      color: $green;
    }
  }

  &__status {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-size: 12px;
    line-height: 1.4;

    &.-spaced {
      margin-top: 10px;
    }

    &.-error {
      color: $ia-color-error;
      font-weight: 600;

      @include dark-theme {
        color: $ia-color-error-text-dark;
      }
    }
  }

  &__loader {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-left: 4px;
  }

  &__dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: currentColor;
    animation: assistantPulse 1.2s ease-in-out infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

.assistant-fade-enter-active,
.assistant-fade-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.assistant-fade-enter-from,
.assistant-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.question-slide-enter-active,
.question-slide-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.question-slide-leave-active {
  position: absolute;
}

.question-slide-enter-from,
.question-slide-leave-to {
  opacity: 0;
  transform: translateX(-6px);
}

@keyframes assistantPulse {
  0%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}

@media (prefers-reduced-motion: reduce) {
  .assistant-fade-enter-active,
  .assistant-fade-leave-active,
  .question-slide-enter-active,
  .question-slide-leave-active {
    transition: opacity 0.01s;
  }

  .assistant-fade-enter-from,
  .assistant-fade-leave-to,
  .question-slide-enter-from,
  .question-slide-leave-to {
    transform: none;
  }

  .writingAssistant__dot {
    animation: none;
  }
}
</style>
